import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth, useUserDisplayName } from '../../services/authContext';

interface UserMenuProps {
  collapsed?: boolean;
}

export function UserMenu({ collapsed = false }: UserMenuProps) {
  const { user, logout, isAdmin, isImpersonating, startImpersonating, stopImpersonating } = useAuth();
  const displayName = useUserDisplayName();
  const [isOpen, setIsOpen] = useState(false);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { data: users } = useQuery({
    queryKey: ['adminUsers', false],
    queryFn: () => import('../../services/api/admin').then(m => m.getAdminUsers(false)),
    enabled: isAdmin,
  });

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowUserPicker(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when picker opens
  useEffect(() => {
    if (showUserPicker && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showUserPicker]);

  if (!user) return null;

  const initials = user.firstName && user.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`
    : user.email[0]?.toUpperCase() || '?';

  const filteredUsers = (users || []).filter((u) => {
    if (u.id === user.id) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
    return fullName.includes(q) || u.email.toLowerCase().includes(q);
  });

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-3 rounded-lg p-2 hover:bg-gray-100 transition-colors ${
          collapsed ? 'justify-center' : 'w-full'
        }`}
        title={collapsed ? displayName : undefined}
      >
        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-medium text-primary-700">{initials}</span>
        </div>
        {!collapsed && (
          <>
            <div className="flex-1 min-w-0 text-left">
              <p className="truncate text-sm font-medium text-gray-900">{displayName}</p>
              <p className="truncate text-xs text-gray-500">{user.email}</p>
            </div>
            <svg
              className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {isOpen && !showUserPicker && (
        <div
          className={`absolute rounded-lg bg-white shadow-lg border border-gray-200 py-1 z-50 ${
            collapsed
              ? 'left-full bottom-0 ml-2 w-48'
              : 'bottom-full left-0 right-0 mb-2'
          }`}
        >
          {/* User info */}
          <div className="px-4 py-2 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">{displayName}</p>
            <p className="text-xs text-gray-500">{user.email}</p>
            {user.roles.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {user.roles.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700"
                  >
                    {role}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Menu items */}
          <div className="py-1">
            <a
              href="/profile"
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => setIsOpen(false)}
            >
              Profile & API Token
            </a>
            {/* MKS-508 — the public company docs reader at /docs. */}
            <a
              href="/docs"
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => setIsOpen(false)}
            >
              Company docs
            </a>
            {isAdmin && (
              <a
                href="/admin"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => setIsOpen(false)}
              >
                Admin Panel
              </a>
            )}
            {isAdmin && !isImpersonating && (
              <button
                onClick={() => setShowUserPicker(true)}
                className="w-full text-left px-4 py-2 text-sm text-amber-700 hover:bg-amber-50"
              >
                Act as user...
              </button>
            )}
            {isImpersonating && (
              <button
                onClick={() => {
                  stopImpersonating();
                  setIsOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-amber-700 hover:bg-amber-50"
              >
                Stop impersonating
              </button>
            )}
            <button
              onClick={() => {
                setIsOpen(false);
                logout();
              }}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              Sign out
            </button>
          </div>
        </div>
      )}

      {isOpen && showUserPicker && (
        <div
          className={`absolute rounded-lg bg-white shadow-lg border border-gray-200 z-50 ${
            collapsed
              ? 'left-full bottom-0 ml-2 w-72'
              : 'bottom-full left-0 right-0 mb-2 w-72'
          }`}
        >
          <div className="p-3 border-b border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900">Act as user</span>
              <button
                onClick={() => {
                  setShowUserPicker(false);
                  setSearchQuery('');
                }}
                className="text-gray-400 hover:text-gray-600 text-xs"
              >
                Back
              </button>
            </div>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            {filteredUsers.length === 0 ? (
              <p className="px-4 py-2 text-sm text-gray-500">No users found</p>
            ) : (
              filteredUsers.map((u) => (
                <button
                  key={u.id}
                  onClick={() => {
                    startImpersonating({
                      id: u.id,
                      email: u.email,
                      firstName: u.firstName,
                      lastName: u.lastName,
                    });
                    setShowUserPicker(false);
                    setSearchQuery('');
                    setIsOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-gray-50 flex flex-col"
                >
                  <span className="text-sm text-gray-900">
                    {[u.lastName, u.firstName].filter(Boolean).join(', ') || u.email}
                  </span>
                  <span className="text-xs text-gray-500">{u.email}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
